import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  Chip,
  IconButton,
  Link,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { durationTracker } from "../../utils/DurationTracker";
import useStore from "../../Store/Store";
import { setURLParam, getURLParam } from "../../utils/URLUtils";
import { useResponsiveValue } from "../../utils/responsive";
import { statsWidgetConfig } from "../../utils/responsive/componentConfig";

// Extend window interface for AI workflow analysis
interface AIWorkflowLogEntry {
  type: string;
  timestamp: string;
  data: any;
  step?: string;
  phase?: string;
}

declare global {
  interface Window {
    aiWorkflowAnalysis?: {
      report: string;
      logs: AIWorkflowLogEntry[];
      workflowId: string;
      timestamp: string;
    };
    aiWorkflowRawLogs?: string[];
    aiWorkflowAnalyses?: {
      report: string;
      logs: AIWorkflowLogEntry[];
      workflowId: string;
      timestamp: string;
    }[];
  }
}

interface StatsWidgetProps {
  isVisible: boolean;
}

interface StatsData {
  sessionName: string;
  totalDuration: number;
  milestones: Array<{
    name: string;
    duration: number;
    timestamp: number;
  }>;
  serverTiming?: {
    totalServerDuration: number;
    aiProviderDuration: number;
    validationTime: number;
    steps: Array<{ name: string; duration: number; timestamp: number }>;
  };
  providerTiming?: {
    totalDuration: number;
    messagePreprocessingStart: number;
    messagePreprocessingEnd: number;
    apiCallStart: number;
    apiCallEnd: number;
    steps: Array<{ name: string; duration: number; timestamp: number }>;
  };
  usageInfo?: {
    modelName?: string;
    promptTokenCount?: number;
    candidatesTokenCount?: number;
    totalTokenCount?: number;
    costUSD?: number;
    pricingModelUsed?: any;
    costCalculationError?: string;
  };
}

const StatsContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  maxHeight: "400px",
  backgroundColor: "rgba(0, 0, 0, 0.85)", // 50% opacity background with better contrast
  borderRadius: "12px",
  padding: "16px",
  zIndex: 2100000, // Highest z-index to be on top of everything including loading status bar
  overflowY: "auto",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",

  // Custom scrollbar
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255, 255, 255, 0.3)",
    borderRadius: "3px",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.5)",
    },
  },
}));

const StatsWidget: React.FC<StatsWidgetProps> = ({ isVisible }) => {
  const [statsHistory, setStatsHistory] = useState<StatsData[]>([]);
  const [activeSessions, setActiveSessions] = useState<string[]>([]);
  const [selectedRepoFilter, setSelectedRepoFilter] = useState<string | "all">(
    "all"
  );
  const [isExpanded, setIsExpanded] = useState(false);

  const { setStatsWidgetVisible } = useStore((state) => ({
    setStatsWidgetVisible: state.setStatsWidgetVisible,
  }));

  // Get responsive configuration
  const responsiveConfig = useResponsiveValue(statsWidgetConfig);

  const handleClose = () => {
    setStatsWidgetVisible(false);
  };

  // Auto-enable verbose debug mode when stats widget becomes visible
  useEffect(() => {
    if (isVisible) {
      const currentDebugMode = getURLParam(window, "debugMode");
      if (currentDebugMode !== "verbose") {
        setURLParam(window, "debugMode", "verbose");
        // Reload the page to apply the new debug mode
        window.location.reload();
      }
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Set up interval to check for new data
    const interval = setInterval(() => {
      // Get active sessions using the public method
      const sessions = durationTracker.getActiveSessions();
      setActiveSessions(sessions);
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible]);

  // Listen for completed sessions (this would be better as an event emitter pattern)
  useEffect(() => {
    if (!isVisible) return;

    const originalCompleteSession =
      durationTracker.completeSession.bind(durationTracker);

    durationTracker.completeSession = (
      sessionId: string,
      finalMilestoneName?: string
    ) => {
      const result = originalCompleteSession(sessionId, finalMilestoneName);

      if (result) {
        const newStats: StatsData = {
          sessionName: result.summary.split('"')[1] || sessionId, // Extract name from summary
          totalDuration: result.totalDuration,
          milestones: result.milestones,
          serverTiming: result.serverTiming,
          providerTiming: result.providerTiming,
          usageInfo: result.usageInfo,
        };

        setStatsHistory((prev) => [newStats, ...prev.slice(0, 9)]); // Keep last 10 sessions
      }

      return result;
    };

    // Cleanup function
    return () => {
      durationTracker.completeSession = originalCompleteSession;
    };
  }, [isVisible]);

  const formatDuration = (seconds: number): string => {
    if (seconds < 1) {
      return `${(seconds * 1000).toFixed(0)}ms`;
    }
    return `${seconds.toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <StatsContainer
      sx={{
        bottom: responsiveConfig.bottom,
        left: responsiveConfig.left,
        width: responsiveConfig.width,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "white",
            fontWeight: "bold",
            flex: 1,
            textAlign: "center",
          }}
        >
          📊 Stats for Nerds
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: "rgba(255, 255, 255, 0.7)",
            "&:hover": {
              color: "white",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {activeSessions.length > 0 && (
        <>
          <Typography
            variant="subtitle2"
            sx={{ color: "#90CAF9", marginBottom: 1 }}
          >
            🔄 Active Sessions ({activeSessions.length})
          </Typography>
          {activeSessions.map((sessionId, index) => {
            const currentDuration =
              durationTracker.getCurrentDuration(sessionId);
            return (
              <Box key={sessionId} sx={{ marginBottom: 1 }}>
                <Chip
                  label={`Session ${index + 1}: ${
                    currentDuration ? formatDuration(currentDuration) : "0s"
                  }`}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(76, 175, 80, 0.3)",
                    color: "white",
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
            );
          })}
          <Divider
            sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginY: 2 }}
          />
        </>
      )}

      <Typography
        variant="subtitle2"
        sx={{ color: "#FFB74D", marginBottom: 1 }}
      >
        📈 Recent Sessions
      </Typography>

      {statsHistory.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: "rgba(255, 255, 255, 0.6)", fontStyle: "italic" }}
        >
          No completed sessions yet. Try generating a simulation diagram or
          making a chat completion!
        </Typography>
      ) : (
        statsHistory.map((stats, index) => (
          <Box
            key={index}
            sx={{
              marginBottom: 2,
              padding: 1.5,
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              borderRadius: 1,
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "white", fontWeight: "bold", marginBottom: 1 }}
            >
              {stats.sessionName}
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: "#81C784", marginBottom: 1 }}
            >
              ⏱️ Total: {formatDuration(stats.totalDuration)}
            </Typography>

            {/* Server Timing Section */}
            {stats.serverTiming && (
              <Box sx={{ marginBottom: 1, marginLeft: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#FFB74D",
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                  }}
                >
                  🖥️ Server Breakdown:
                </Typography>
                <Box sx={{ marginLeft: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontSize: "0.75rem",
                    }}
                  >
                    Total Server:{" "}
                    {formatDuration(stats.serverTiming.totalServerDuration)}
                  </Typography>
                  <br />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontSize: "0.75rem",
                    }}
                  >
                    AI Provider:{" "}
                    {formatDuration(stats.serverTiming.aiProviderDuration)}
                  </Typography>
                  <br />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontSize: "0.75rem",
                    }}
                  >
                    Validation:{" "}
                    {formatDuration(stats.serverTiming.validationTime)}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Provider Timing Section */}
            {stats.providerTiming && (
              <Box sx={{ marginBottom: 1, marginLeft: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#9C27B0",
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                  }}
                >
                  🤖 AI Provider Breakdown:
                </Typography>
                <Box sx={{ marginLeft: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontSize: "0.75rem",
                    }}
                  >
                    Total Provider:{" "}
                    {formatDuration(stats.providerTiming.totalDuration)}
                  </Typography>
                  <br />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontSize: "0.75rem",
                    }}
                  >
                    Preprocessing:{" "}
                    {formatDuration(
                      (stats.providerTiming.messagePreprocessingEnd -
                        stats.providerTiming.messagePreprocessingStart) /
                        1000
                    )}
                  </Typography>
                  <br />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontSize: "0.75rem",
                    }}
                  >
                    API Call:{" "}
                    {formatDuration(
                      (stats.providerTiming.apiCallEnd -
                        stats.providerTiming.apiCallStart) /
                        1000
                    )}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Usage Information Section */}
            {stats.usageInfo && (
              <Box sx={{ marginBottom: 1, marginLeft: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#FF9800",
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                  }}
                >
                  📊 Usage & Cost:
                </Typography>
                <Box sx={{ marginLeft: 1 }}>
                  {stats.usageInfo.modelName && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "0.75rem",
                        }}
                      >
                        Model: {stats.usageInfo.modelName}
                      </Typography>
                      <br />
                    </>
                  )}
                  {stats.usageInfo.totalTokenCount && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "0.75rem",
                        }}
                      >
                        Total Tokens:{" "}
                        {stats.usageInfo.totalTokenCount.toLocaleString()}
                      </Typography>
                      <br />
                    </>
                  )}
                  {stats.usageInfo.promptTokenCount && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "0.75rem",
                        }}
                      >
                        Prompt:{" "}
                        {stats.usageInfo.promptTokenCount.toLocaleString()}
                      </Typography>
                      <br />
                    </>
                  )}
                  {stats.usageInfo.candidatesTokenCount && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: "0.75rem",
                        }}
                      >
                        Response:{" "}
                        {stats.usageInfo.candidatesTokenCount.toLocaleString()}
                      </Typography>
                      <br />
                    </>
                  )}
                  {stats.usageInfo.costUSD !== undefined &&
                    stats.usageInfo.costUSD !== null && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#4CAF50",
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                          }}
                        >
                          Cost: ${stats.usageInfo.costUSD.toFixed(4)} USD
                        </Typography>
                        <br />
                      </>
                    )}
                  {stats.usageInfo.costCalculationError && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#f44336",
                          fontSize: "0.7rem",
                        }}
                      >
                        Cost Error: {stats.usageInfo.costCalculationError}
                      </Typography>
                      <br />
                    </>
                  )}
                </Box>
              </Box>
            )}

            {/* AI Workflow Analyses (support multiple workflows) */}
            {(window as any).aiWorkflowAnalyses &&
              Array.isArray((window as any).aiWorkflowAnalyses) &&
              (window as any).aiWorkflowAnalyses.length > 0 && (
                <Box sx={{ marginBottom: 1, marginLeft: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#E91E63",
                      fontWeight: "bold",
                      fontSize: "0.8rem",
                    }}
                  >
                    🧠 AI Workflow Analyses:
                  </Typography>
                  {(window as any).aiWorkflowAnalyses
                    .slice()
                    .reverse() // show latest first
                    .map((analysis: any, aIdx: number) => (
                      <Box
                        key={analysis.workflowId || aIdx}
                        sx={{ ml: 1, mb: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255, 255, 255, 0.8)",
                            fontSize: "0.75rem",
                          }}
                        >
                          {aIdx + 1}. {analysis.workflowId}
                        </Typography>
                        <br />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#81C784",
                            fontSize: "0.75rem",
                            textDecoration: "underline",
                            cursor: "pointer",
                            mr: 1,
                          }}
                          onClick={() => {
                            const blob = new Blob([analysis.report], {
                              type: "text/markdown",
                            });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `ai-workflow-analysis-${analysis.workflowId}.md`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          }}
                        >
                          📋 Download Report
                        </Typography>
                        {Array.isArray(window.aiWorkflowRawLogs) &&
                          window.aiWorkflowRawLogs.length > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#FF9800",
                                fontSize: "0.75rem",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                const rawLogsText =
                                  window.aiWorkflowRawLogs.join("\n");
                                const blob = new Blob([rawLogsText], {
                                  type: "text/plain",
                                });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `ai-workflow-raw-logs-${analysis.workflowId}.txt`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              }}
                            >
                              📄 Download Raw Logs
                            </Typography>
                          )}
                      </Box>
                    ))}
                </Box>
              )}

            {/* Client-side Milestones */}
            {stats.milestones.length > 0 && (
              <Box sx={{ marginLeft: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64B5F6",
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                    marginBottom: 0.5,
                  }}
                >
                  📱 Client-side Steps:
                </Typography>
                {stats.milestones.map((milestone, mIndex) => (
                  <Box
                    key={mIndex}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 0.5,
                      marginLeft: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255, 255, 255, 0.8)",
                        fontSize: "0.75rem",
                        flex: 1,
                        marginRight: 1,
                      }}
                    >
                      {mIndex + 1}. {milestone.name}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#FFA726",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                        }}
                      >
                        +{formatDuration(milestone.duration)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255, 255, 255, 0.5)",
                          fontSize: "0.65rem",
                        }}
                      >
                        {formatTimestamp(milestone.timestamp)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))
      )}
    </StatsContainer>
  );
};

export default StatsWidget;
